// src/hooks/useUserTimezone.ts
"use client";

import { useEffect, useState } from "react";

export type UserTimezoneInfo = {
  /** IANA zone, e.g. "Asia/Dhaka" */
  iana: string;
  /** Human-friendly long name, e.g. "Bangladesh Standard Time" */
  nameLong: string;
  /** Short name, e.g. "BST" or "GMT+6" (varies by platform/locale) */
  nameShort: string;
  /** UTC label, e.g. "UTC+06:00" */
  offsetLabel: string;
  /** Minutes offset from UTC, e.g. +360 for Dhaka */
  offsetMinutes: number;
  /** Combined, human-friendly description */
  label: string; // e.g. "Bangladesh Standard Time (BST) • Asia/Dhaka • UTC+06:00"
};

function readIana(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof tz === "string" && tz) return tz;
  } catch {}
  return "UTC";
}

function getZoneNames(iana: string, date = new Date()) {
  try {
    const longFmt = new Intl.DateTimeFormat(undefined, {
      timeZone: iana,
      hour: "2-digit",
      timeZoneName: "long",
    }).formatToParts(date);
    const shortFmt = new Intl.DateTimeFormat(undefined, {
      timeZone: iana,
      hour: "2-digit",
      timeZoneName: "short",
    }).formatToParts(date);

    const nameLong = longFmt.find((p) => p.type === "timeZoneName")?.value || iana;
    const nameShort = shortFmt.find((p) => p.type === "timeZoneName")?.value || "UTC";
    return { nameLong, nameShort };
  } catch {
    return { nameLong: iana, nameShort: "UTC" };
  }
}

function buildOffsetLabel(offsetMinutes: number) {
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
}

function composeLabel({
  nameLong,
  nameShort,
  iana,
  offsetLabel,
}: Pick<UserTimezoneInfo, "nameLong" | "nameShort" | "iana" | "offsetLabel">) {
  const short = nameShort && nameShort !== nameLong ? ` (${nameShort})` : "";
  return `${nameLong}${short} • ${iana} • ${offsetLabel}`;
}

/** Returns IANA id, long/short names, UTC offset label, and a single human-friendly label string. */
export default function useUserTimezone(
  defaults?: Partial<UserTimezoneInfo>
): UserTimezoneInfo {
  const [info, setInfo] = useState<UserTimezoneInfo>({
    iana: defaults?.iana ?? "UTC",
    nameLong: defaults?.nameLong ?? "Coordinated Universal Time",
    nameShort: defaults?.nameShort ?? "UTC",
    offsetMinutes: defaults?.offsetMinutes ?? 0,
    offsetLabel: defaults?.offsetLabel ?? "UTC+00:00",
    label:
      defaults?.label ??
      "Coordinated Universal Time (UTC) • UTC • UTC+00:00",
  });

  useEffect(() => {
    const apply = () => {
      const iana = readIana();
      const offsetMinutes = -new Date().getTimezoneOffset(); // minutes ahead of UTC
      const offsetLabel = buildOffsetLabel(offsetMinutes);
      const { nameLong, nameShort } = getZoneNames(iana);
      setInfo({
        iana,
        nameLong,
        nameShort,
        offsetMinutes,
        offsetLabel,
        label: composeLabel({ nameLong, nameShort, iana, offsetLabel }),
      });
    };
    const kick = window.setTimeout(apply, 0);

    // Update names if user changes language
    const onLangChange = () => {
      setInfo((prev) => {
        const { nameLong, nameShort } = getZoneNames(prev.iana);
        return {
          ...prev,
          nameLong,
          nameShort,
          label: composeLabel({
            nameLong,
            nameShort,
            iana: prev.iana,
            offsetLabel: prev.offsetLabel,
          }),
        };
      });
    };
    window.addEventListener("languagechange", onLangChange);
    return () => {
      window.clearTimeout(kick);
      window.removeEventListener("languagechange", onLangChange);
    };
  }, []);

  return info;
}
